import { HashLocationStrategy, LocationStrategy } from "@angular/common";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { ErrorHandler, Injector, NgModule, inject, provideAppInitializer } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggleDefaultOptions } from "@angular/material/button-toggle";
import { MAT_DATE_LOCALE, MatNativeDateModule } from "@angular/material/core";
import { MatIconModule } from "@angular/material/icon";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { Router, RouterModule } from "@angular/router";
import { AkitaNgRouterStoreModule } from "@datorama/akita-ng-router-store";
import { AkitaNgDevtools } from "@datorama/akita-ngdevtools";
import { TranslateLoader, TranslateModule, TranslateService } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { NgxFormGeneratorModule } from "@recursyve/ngx-form-generator";
import { NgxPankoModule } from "@recursyve/ngx-panko";
import { NiceAdvancedFiltersModule, NiceBaseListModule, NiceFilterViewModule } from "@recursyve/nice-data-filter-kit";
import {
    NiceConfigModule,
    NiceLoadingToastModule,
    NiceModule,
    NiceSweetAlertModule,
    NiceToastModule
} from "@recursyve/nice-ui-kit.v2";
import * as Sentry from "@sentry/angular";
import { provideNgxMask } from "ngx-mask";
import { of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../environments/environment";
import { ApiModule } from "./api/api.module";
import { AppComponent } from "./app.component";
import { ROUTES } from "./app.routes";
import { CustomAlertComponent } from "./components/custom-alert/custom-alert.component";
import { CustomAlertModule } from "./components/custom-alert/custom-alert.module";
import { DashboardModule } from "./features/dashboard/dashboard.module";
import { RegisterModule } from "./features/register/register.module";
import { UnauthorizedModule } from "./features/unauthorized/unauthorized.module";
import { FlagsmithModule } from "./flagsmith/flagsmith.module";
import { AuthenticatedGuard } from "./guards/authenticated.guard";
import { NotAuthenticatedGuard } from "./guards/not-authenticated.guard";
import { GlobalErrorHandler } from "./handlers/global-error.handler";
import { PipesModule } from "./pipes/pipes.module";
import { NerriBreadcrumbRouteResolver } from "./providers/breadcrumb-route.resolver";
import { FilterGroupsService } from "./providers/filter-groups.service";
import { I18nService } from "./providers/i18n.service";
import { ServiceWorkerModule } from "./service-worker/service-worker.module";
import { AppQuery } from "./store/app.query";
import { AppStore } from "./store/app.store";

export function appInitializerFactory(translate: TranslateService, i18nService: I18nService) {
    return () => {
        const lang = i18nService.getDefaultLang();
        i18nService.setLanguage(lang);

        return translate.use(lang).pipe(
            map(() => null),
            catchError(err => {
                Sentry.captureException(err);
                return of(null);
            })
        );
    };
}

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http);
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        RouterModule.forRoot(ROUTES, {
            anchorScrolling: "enabled",
            scrollPositionRestoration: "enabled",
            bindToComponentInputs: true
        }),
        FlagsmithModule,
        NiceModule,
        NiceConfigModule.forRoot({
            theme: "default",
            scheme: "light"
        }),
        NgxPankoModule.forRoot({ routeResolver: NerriBreadcrumbRouteResolver }),
        CustomAlertModule,
        NiceSweetAlertModule.forRoot({
            customModal: CustomAlertComponent
        }),
        NiceToastModule.forRoot({ appearance: "border" }),
        NiceLoadingToastModule.forRoot({ appearance: "border" }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            },
            defaultLanguage: "fr"
        }),
        NiceBaseListModule.forRoot(),
        NiceFilterViewModule.forRoot(),
        NiceAdvancedFiltersModule.forRoot({
            icons: {
                add: { svgIcon: "streamline-regular:add" },
                close: { svgIcon: "streamline-regular:remove" },
                delete: { svgIcon: "streamline-regular:bin-1" },
                refresh: { svgIcon: "streamline-regular:synchronize-refresh-arrow-2" },
                arrowDown: { svgIcon: "streamline-regular:arrow-down-1" },
                datePicker: { svgIcon: "streamline-regular:calendar" }
            },
            filterGroupService: FilterGroupsService
        }),
        NgxFormGeneratorModule.forRoot(),
        MatNativeDateModule,
        MatButtonModule,
        MatIconModule,
        ApiModule,
        DashboardModule,
        RegisterModule,
        UnauthorizedModule,
        PipesModule,
        environment.production ? [] : AkitaNgDevtools.forRoot(),
        AkitaNgRouterStoreModule,
        ServiceWorkerModule
    ],
    providers: [
        provideNgxMask(),
        AppStore,
        AppQuery,
        I18nService,
        AuthenticatedGuard,
        NotAuthenticatedGuard,
        { provide: LocationStrategy, useClass: HashLocationStrategy },
        {
            provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
            useValue: {
                hideSingleSelectionIndicator: true,
                hideMultipleSelectionIndicator: true
            } satisfies MatButtonToggleDefaultOptions
        },
        {
            provide: MatPaginatorIntl,
            useFactory: (i18nService: I18nService) => i18nService.getPaginatorIntl(),
            deps: [I18nService]
        },
        {
            provide: MAT_DATE_LOCALE,
            useFactory: (i18nService: I18nService) => i18nService.getDatePickLang(),
            deps: [I18nService]
        },
        provideAppInitializer(() => appInitializerFactory(inject(TranslateService), inject(I18nService))()),
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        { provide: Sentry.TraceService, deps: [Router] },
        provideAppInitializer(() => {
            inject(Sentry.TraceService);
        })
    ],
    exports: [AppComponent],
    bootstrap: [AppComponent]
})
export class AppModule {}
